import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FilterState } from '@/types/filter';
import Drawer, { DRAWER_CLOSE_ANIMATION_DURATION_MS } from '@/storybook/components/Drawer/Drawer';
import Scrollable from '@/storybook/components/ScrollArea/Scrollable';
import { CES_DROPDOWN_TRIGGER_SELECTOR } from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import styles from './FiltersOverlay.module.css';
import { ScientificActivitiesView } from './components/ScientificActivitiesView';
import { StandardView } from './components/StandardView';
import { FiltersSidebar } from './components/FiltersSidebar';
import { FiltersFooter } from './components/FiltersFooter';
import { useVisibleFilterSections } from './hooks/useVisibleFilterSections';
import { FiltersFormProps } from './types/FiltersForm.types';

const FORM_STYLE = { height: '100%' };

function focusFirstFilterControl(container: HTMLElement) {
  const dropdowns = container.querySelectorAll<HTMLElement>(CES_DROPDOWN_TRIGGER_SELECTOR);
  for (const el of dropdowns) {
    if (el.getAttribute('data-is-disabled') === 'true') continue;
    el.focus({ preventScroll: true });
    return;
  }
  const fallback = container.querySelector<HTMLElement>(
    'input:not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled])',
  );
  fallback?.focus({ preventScroll: true });
}

export const FiltersForm: React.FC<FiltersFormProps> = ({
  isOpen,
  activeSection,
  setActiveSection,
  handleClearSection,
  handleCloseDrawer,
  handleCancel,
  handleClearAll,
  onSubmit,
  canSubmit,
  hasDraft,
}) => {
  const { t } = useTranslation();
  const visibleSections = useVisibleFilterSections();

  const { control, handleSubmit } = useFormContext<FilterState>();

  const filterSectionContentRef = useRef<HTMLDivElement>(null);
  const [shouldRenderSectionContent, setShouldRenderSectionContent] = useState(isOpen);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const container = filterSectionContentRef.current;
    if (!container) return;

    const run = () => {
      focusFirstFilterControl(container);
    };

    const frameId = requestAnimationFrame(run);
    return () => cancelAnimationFrame(frameId);
  }, [activeSection, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setShouldRenderSectionContent(true);
      return undefined;
    }

    if (!shouldRenderSectionContent) return undefined;

    const timeoutId = window.setTimeout(() => {
      setShouldRenderSectionContent(false);
    }, DRAWER_CLOSE_ANIMATION_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, shouldRenderSectionContent]);

  const activeSectionConfig = visibleSections.find(section => section.id === activeSection);
  const isClosing = !isOpen && shouldRenderSectionContent;
  const shouldRenderActiveSection = isOpen || isClosing;

  const contentClassName = [
    styles.filterContentInner,
    activeSection === 'characteristics' && styles.sectionCharacteristics,
    activeSection === 'segmentations' && styles.sectionSegmentations,
    activeSection === 'scientificActivities' && styles.sectionScientific,
    activeSection === 'recommendations' && styles.sectionRecommendations,
    activeSection === 'scientificActivities' && styles.scientificActivitiesLayout,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleCloseDrawer}
      title={t('global.filters.drawerHeading')}
      hideButtonLabel={t('global.filters.hideButtonLabel')}
      position="top"
      footerContent={
        <FiltersFooter
          onClearAll={handleClearAll}
          onCancel={handleCancel}
          formId="filters-form"
          canSubmit={canSubmit}
          hasDraft={hasDraft}
        />
      }
      className={styles.customGlobalFiltersDrawer}
      contentClassName={styles.customDrawerContent}
      data-testid="filters-drawer"
    >
      <form id="filters-form" onSubmit={handleSubmit(onSubmit)} style={FORM_STYLE}>
        <div className={styles.filterBody}>
          <div className={styles.filterBodyWrapper}>
            <FiltersSidebar
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              handleClearSection={handleClearSection}
            />

            <div
              className={[
                styles.filterContent,
                (activeSection === 'scientificActivities' ||
                  activeSection === 'recommendations' ||
                  activeSection === 'segmentations' ||
                  activeSection === 'characteristics') &&
                  styles.filterContentNoScroll,
                activeSection === 'characteristics' && styles.filterContentCharacteristics,
                activeSection === 'segmentations' && styles.filterContentSegmentations,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <Scrollable>
                <div ref={filterSectionContentRef} className={contentClassName}>
                  {shouldRenderActiveSection &&
                    activeSectionConfig &&
                    (activeSection === 'scientificActivities' ? (
                      <ScientificActivitiesView control={control} />
                    ) : (
                      <StandardView config={activeSectionConfig} control={control} />
                    ))}
                </div>
              </Scrollable>
            </div>
          </div>
        </div>
      </form>
    </Drawer>
  );
};
